'use client';

import { useAppRouter } from '@fdc/hooks/useAppRouter';
import {
  getBreadcrumbLink,
  mapSlugToBreadcrumbs,
} from '@fdc/utils/get-breadcrumbs';
import {
  CoreBreadcrumb,
  CoreBreadcrumbItem,
  CoreBreadcrumbProps,
} from '@woolworthslimited/core-components-react/breadcrumb';
import { useId } from 'react';

import styles from './breadcrumb.module.scss';

const SITE_ORIGIN = 'https://www.woolworths.co.nz';

const toAbsoluteUrl = (path: string) => {
  const url = new URL(path, SITE_ORIGIN);
  url.search = '';
  url.hash = '';
  return url.toString();
};

export const Breadcrumb = ({
  slug,
  labelMap,
  prefix = '',
}: {
  slug: string[];
  labelMap?: Record<string, string>;
  prefix?: string;
}) => {
  const router = useAppRouter();

  const subBreadcrumbs = slug.map((slg, index) => {
    const title = labelMap?.[slg];
    const subBreadcrumbProps = mapSlugToBreadcrumbs(
      slg,
      index,
      { slug },
      prefix,
      title
    );

    return {
      ...subBreadcrumbProps,
      onClick: (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        props: CoreBreadcrumbItem
      ) => {
        e.preventDefault();
        router.push(props.link.url);
      },
    };
  });

  const homeBreadcrumb = getBreadcrumbLink('Home', '/', false);

  const coreBreadcrumbProps: CoreBreadcrumbProps = {
    id: useId(),
    items: [
      {
        ...homeBreadcrumb,
        onClick: (e, _) => {
          e.preventDefault();
          router.push('/');
        },
      },
      ...subBreadcrumbs,
    ],
  };

  const breadcrumbListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [homeBreadcrumb, ...subBreadcrumbs].map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title,
      ...(!item.current && { item: toAbsoluteUrl(item.link.url) }),
    })),
  };

  return (
    <div className={styles.breadcrumb}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbListJsonLd),
        }}
      />
      <CoreBreadcrumb {...coreBreadcrumbProps} />
    </div>
  );
};
